import { APITypesV1 } from "@cur8/api-client";
import {
  Button,
  Icon16Alert,
  Icon16Checkmark,
  Icon24ArrowRight,
  Typography,
} from "@cur8/maneki";
import { useClearRoomControllerMutation } from "hooks/mutations/useClearRoomControllerMutation";
import { useShowNekoLens } from "hooks/useShowNekoLens";
import { useBloodWorkModeQuery } from "hooks/queries/useBloodWorkModeQuery";
import { useECGDeviceQuery } from "hooks/queries/useECGDeviceQuery";
import { useVisitBloodSamplesQuery } from "hooks/queries/useVisitBloodSamplesQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { readScanStateFromVisit } from "lib/readScanStateFromVisit";
import { CheckInParams } from "lib/types/checkInParameters";
import { useCallback, useMemo } from "react";
import { paths } from "render/routes/paths";
import { InformationLayout } from "render/ui/layout/InformationLayout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent/PageFrameContent";
import { List } from "render/ui/presentation/List";
import { Link } from "render/ui/trigger/Link";
import { isBloodWorkComplete } from "render/views/AssignVials/scanDecision";
import { useCardioSecurDeeplink } from "render/views/CheckInCompleteView/hooks/useCardioSecurDeeplink";
import styles from "./styles.module.sass";
type CheckInCompleteViewProps = {
  goTo(url: string): void;
} & CheckInParams;

export function CheckInCompleteView({
  goTo,
  patientId,
  visitId,
}: CheckInCompleteViewProps) {
  const { mutate: clearRoomController } = useClearRoomControllerMutation();
  const { data: visit } = useVisitQuery({
    patientId,
    visitId,
    options: { refetchInterval: 5000 },
  });
  const showNekoLens = useShowNekoLens({ patientId, visitId });
  const skinScanState = readScanStateFromVisit("skin", visit);
  const cardioScanState = readScanStateFromVisit("cardio", visit);
  const tissueScanState = readScanStateFromVisit("tci", visit);
  const ecgScanState = readScanStateFromVisit("ecg", visit);

  const { data: bloodWorkModeData } = useBloodWorkModeQuery({
    patientId,
    visitId,
  });
  const bloodWorkMode = bloodWorkModeData?.bloodWorkMode;

  const goToNext = useCallback(() => {
    clearRoomController(
      { patientId, visitId },
      { onSuccess: () => goTo(paths.root.url({})) }
    );
  }, [clearRoomController, goTo, patientId, visitId]);

  const goToNekoLensAuth = useCallback(() => {
    const url = paths.nekoLensAuth.url({ patientId, visitId });
    goTo(url);
  }, [goTo, patientId, visitId]);

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <Typography variant="display-s">Scan progress</Typography>
            <List>
              <List.Item>
                <div className={styles.scanItem}>
                  <div className={styles.itemTitle}>
                    <Typography variant="title-s">Skin Scan</Typography>
                    <Link
                      onPress={() =>
                        goTo(
                          paths.assignToSkinScanner.url({
                            patientId,
                            visitId,
                          })
                        )
                      }
                    >
                      Re-assign member
                    </Link>
                  </div>
                  {skinScanState != null ? (
                    <ScanStatusBadge scanState={skinScanState} />
                  ) : undefined}
                </div>
              </List.Item>
              <List.Divider />
              <List.Item>
                <div className={styles.scanItem}>
                  <div className={styles.itemTitle}>
                    <Typography variant="title-s">Tissue Scan</Typography>
                  </div>
                  {tissueScanState != null ? (
                    <ScanStatusBadge scanState={tissueScanState} />
                  ) : undefined}
                </div>
              </List.Item>
              <List.Divider />
              <List.Item>
                <ECGListItem
                  goTo={goTo}
                  ecgScanState={ecgScanState}
                  patientId={patientId}
                  visitId={visitId}
                />
              </List.Item>
              <List.Divider />
              <List.Item>
                <div className={styles.scanItem}>
                  <div className={styles.itemTitle}>
                    <Typography variant="title-s">Cardio Scan</Typography>
                  </div>
                  {cardioScanState != null ? (
                    <ScanStatusBadge scanState={cardioScanState} />
                  ) : undefined}
                </div>
              </List.Item>
              {bloodWorkMode === APITypesV1.BloodWorkMode.BarcodeAssignment ? (
                <>
                  <List.Divider />
                  <List.Item>
                    <BloodWorkListItem
                      goTo={goTo}
                      patientId={patientId}
                      visitId={visitId}
                    />
                  </List.Item>
                </>
              ) : undefined}
              {showNekoLens ? (
                <>
                  <List.Divider />
                  <List.Item>
                    <div className={styles.scanItem}>
                      <div className={styles.itemTitle}>
                        <Typography variant="title-s">Derma</Typography>
                        <div className={styles.itemActions}>
                          <Link onPress={goToNekoLensAuth}>Show QR code</Link>
                        </div>
                      </div>
                    </div>
                  </List.Item>
                </>
              ) : undefined}
            </List>
            <div className={styles.divider} />
            <section className={styles.checkoutSection}>
              <div className={styles.checkoutText}>
                <Typography variant="title-l">Ready to check out?</Typography>
                <Typography variant="body-m" color="subtle">
                  Check out if all examinations are complete
                </Typography>
              </div>
              <Button
                onPress={goToNext}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                Check Out
              </Button>
            </section>
          </section>
        }
      />
    </PageFrameContent>
  );
}

type ScanStatusBadgeProps = {
  scanState: APITypesV1.ImmutableScanState | undefined;
};

type ScanStatusBadgeHighlight = "success" | "error";

function ECGListItem({
  patientId,
  ecgScanState,
  visitId,
  goTo,
}: {
  visitId: string;
  patientId: string;
  ecgScanState: APITypesV1.ImmutableScanState | undefined;
  goTo(url: string): void;
}) {
  const eCGDeviceQuery = useECGDeviceQuery({ patientId, visitId });
  const openCardioSecurApp = useCardioSecurDeeplink({
    patientId,
    visitId,
  });
  const data = useMemo(() => {
    if (eCGDeviceQuery.error) {
      return {
        label: "Could not fetch ECG device (try again?)",
        onClick: () => {
          if (eCGDeviceQuery.isFetching) {
            return;
          }
          const refetch = eCGDeviceQuery.refetch;
          refetch();
        },
      };
    }
    switch (eCGDeviceQuery.data?.deviceType) {
      case APITypesV1.EcgType.EcgCardiosecur:
        return {
          label:
            ecgScanState === APITypesV1.ImmutableScanState.Finished
              ? "Re-assign member"
              : "Assign to CardioSecur",
          onClick: () => openCardioSecurApp(),
        };
      case APITypesV1.EcgType.EcgNorav:
        return {
          label:
            eCGDeviceQuery.data?.assignmentStatus ===
            APITypesV1.DeviceAssignmentStatus.AssignedToMember
              ? "Re-assign member"
              : "Assign to Norav",
          onClick: () =>
            goTo(paths.assignToNoravECGDevice.url({ patientId, visitId })),
        };
      default:
        return undefined;
    }
  }, [
    eCGDeviceQuery.isFetching,
    eCGDeviceQuery.refetch,
    eCGDeviceQuery.error,
    eCGDeviceQuery.data,
    ecgScanState,
    goTo,
    openCardioSecurApp,
    patientId,
    visitId,
  ]);

  return (
    <div
      data-testid="check-in-complete-ecg-section"
      className={styles.scanItem}
    >
      <div className={styles.itemTitle}>
        <Typography variant="title-s">ECG (12-lead)</Typography>
        <div className={styles.itemActions}>
          <Link onPress={data?.onClick}>{data?.label}</Link>
          <Typography variant="body-m" color="subtle">
            ({patientId})
          </Typography>
        </div>
      </div>
      {ecgScanState != null ? (
        <ScanStatusBadge scanState={ecgScanState} />
      ) : undefined}
    </div>
  );
}

function BloodWorkListItem({
  goTo,
  patientId,
  visitId,
}: {
  goTo(url: string): void;
  patientId: string;
  visitId: string;
}) {
  const bloodSamplesQuery = useVisitBloodSamplesQuery({ patientId, visitId });

  const allAssigned = isBloodWorkComplete(bloodSamplesQuery.data?.bloodSamples);

  const goToAssignVials = useCallback(() => {
    goTo(paths.assignVials.url({ patientId, visitId }));
  }, [goTo, patientId, visitId]);

  return (
    <div className={styles.scanItem}>
      <div className={styles.itemTitle}>
        <Typography variant="title-s">Blood Work</Typography>
        <Link onPress={goToAssignVials}>
          {allAssigned ? "Re-assign vials" : "Assign vials"}
        </Link>
      </div>
      {allAssigned ? (
        <ScanStatusBadge scanState={APITypesV1.ImmutableScanState.Finished} />
      ) : undefined}
    </div>
  );
}

function ScanStatusBadge({ scanState }: ScanStatusBadgeProps) {
  const { label, highlight } = useMemo<{
    label: string;
    highlight?: ScanStatusBadgeHighlight;
  }>(() => {
    switch (scanState) {
      case APITypesV1.ImmutableScanState.Finished:
        return {
          label: "Completed",
          highlight: "success",
        };
      case APITypesV1.ImmutableScanState.Inprogress:
        return {
          label: "In progress",
          highlight: undefined,
        };
      case APITypesV1.ImmutableScanState.Available:
        return {
          label: "Ready",
          highlight: undefined,
        };
      case APITypesV1.ImmutableScanState.Failed:
        return {
          label: "Missing",
          highlight: "error",
        };
      default:
        return {
          label: "",
          highlight: undefined,
        };
    }
  }, [scanState]);

  return (
    <div className={styles.scanStatusBadge} data-highlight={highlight}>
      {highlight === "success" && <Icon16Checkmark />}
      {highlight === "error" && <Icon16Alert />}
      <Typography variant="eyebrow-m" as="span">
        {label}
      </Typography>
    </div>
  );
}
